<template>
  <v-container fluid class="fill-height pa-0">
    <v-row no-gutters class="fill-height">
      <!-- Left controls panel -->
      <v-col cols="12" md="3" class="pa-3" style="max-height:100vh; overflow:auto;">
        <v-card flat>
          <v-card-title>Graph Controls</v-card-title>
          <v-card-text>
            <v-switch v-model="showNodeLabels" label="Show node labels" />
            <v-divider class="my-3" />
            <div class="mb-2">
              <strong>Classes (node types)</strong>
              <v-btn text small @click="selectAllClasses">All</v-btn>
              <v-btn text small @click="clearAllClasses">None</v-btn>
            </div>
            <v-list dense>
              <v-list-item v-for="c in availableClasses" :key="c" class="d-flex align-center flex-nowrap">
                <v-list-item-action>
                  <v-checkbox v-model="selectedClasses" :value="c" hide-details density="compact"/>
                  <v-list-item-title>{{ c }}</v-list-item-title>
                </v-list-item-action>
              </v-list-item>
            </v-list>

            <v-divider class="my-3" />

            <div class="mb-2">
              <strong>Edge labels (properties)</strong>
              <v-btn text small @click="selectAllEdgeLabels">All</v-btn>
              <v-btn text small @click="clearAllEdgeLabels">None</v-btn>
            </div>
            <v-list dense>
              <v-list-item v-for="e in availableEdgeLabels" :key="e" class="d-flex align-center flex-nowrap">
                <v-list-item-action>
                  <v-checkbox v-model="selectedEdgeLabels" :value="e" hide-details density="compact" />
                  <v-list-item-title>{{ e }}</v-list-item-title>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Main graph area -->
      <v-col cols="12" md="9" class="pa-0">
        <div ref="graphContainer" class="graph-container"></div>
        <!-- Node info modal -->
        <v-dialog v-model="isModalOpen" max-width="520px">
          <v-card>
            <v-card-title>{{ selectedNode?.label }}</v-card-title>
            <v-card-text>
              <p><strong>Type:</strong> {{ selectedNode?.type }}</p>
              <p>
                <strong>Wikidata:</strong>
                <a :href="selectedNode?.id" target="_blank" rel="noopener noreferrer">{{ selectedNode?.id }}</a>
              </p>
              <p v-if="selectedNode?.wookipedia_url">
                <strong>Wookieepedia:</strong>
                <a :href="selectedNode?.wookipedia_url" target="_blank" rel="noopener noreferrer">{{ selectedNode?.wookipedia_url }}</a>
              </p>
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn text @click="isModalOpen = false">Close</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import apiClient from "@/apiClient";
import { ref, onMounted, onUnmounted, watch, computed } from "vue";
import * as d3 from "d3";
import { useHead } from '@unhead/vue';

useHead({
  title: "Star Wars Galaxy Graph – Visualize Connections Between Planets & Characters",
  meta: [
    {
      name: "description",
      content: "Explore the Star Wars universe with our interactive graph viewer. Visualize connections between planets, characters, factions, and more. Click nodes to uncover detailed lore, relationships, and Wikidata-powered insights.",
    },
    {
      property: "og:title",
      content: "Star Wars Galaxy Graph – Visualize Connections Between Planets & Characters",
    },
    {
      property: "og:description",
      content: "Dive into the Star Wars galaxy using our interactive graph. Discover relationships between planets, characters, and factions, with detailed insights powered by Wikidata.",
    },
    {
      property: "og:url",
      content: "https://sw-holonet.com/graphviewer",
    },
    { property: "og:type", content: "website" },
    {
      name: "twitter:title",
      content: "Star Wars Galaxy Graph – Visualize Connections Between Planets & Characters",
    },
    {
      name: "twitter:description",
      content: "Visualize the Star Wars universe with our interactive graph viewer. Explore connections between planets, characters, and factions, powered by Wikidata.",
    },
    { name: "twitter:card", content: "summary" },
    {
      name: "keywords",
      content: "Star Wars graph, Star Wars connections, Star Wars interactive map, Star Wars lore, Wikidata Star Wars, Star Wars planets, Star Wars characters, Star Wars factions, galaxy graph viewer",
    },
  ],
});


const graphContainer = ref(null);
const selectedNode = ref(null);
const isModalOpen = ref(false);

const showNodeLabels = ref(true);
const availableClasses = ref([]);
const availableEdgeLabels = ref([]);
const selectedClasses = ref([]); // array of enabled classes
const selectedEdgeLabels = ref([]); // array of enabled edge labels

let rawNodes = [];
let rawLinks = [];

// keep D3 selections accessible for visibility updates
let svg, graphGroup, linkSel, nodeSel, labelsSel;
const edgeColorMap = {
  "present in work": "#d8dfe1",
  "homeworld": "#528db1",
  "default": "#aaa"
};

const fetchData = async () => {
  try {
    const res = await apiClient.get("/api/graph");
    return res.data || { nodes: [], links: [] };
  } catch (e) {
    console.error(e);
    return { nodes: [], links: [] };
  }
};

const buildFiltersFromData = (nodes, links) => {
  const classes = Array.from(new Set(nodes.map(n => n.type).filter(Boolean))).sort();
  const edgeLabels = Array.from(new Set(links.map(l => l.label).filter(Boolean))).sort();
  availableClasses.value = classes;
  availableEdgeLabels.value = edgeLabels;
  // default: select all
  if (selectedClasses.value.length === 0) selectedClasses.value = [...classes];
  if (selectedEdgeLabels.value.length === 0) selectedEdgeLabels.value = [...edgeLabels];
};

const selectAllClasses = () => (selectedClasses.value = [...availableClasses.value]);
const clearAllClasses = () => (selectedClasses.value = []);
const selectAllEdgeLabels = () => (selectedEdgeLabels.value = [...availableEdgeLabels.value]);
const clearAllEdgeLabels = () => (selectedEdgeLabels.value = []);

// main draw
const drawGraph = async () => {
  const data = await fetchData();
  rawNodes = (data.nodes || []).map(n => ({ ...n })); // copy
  rawLinks = (data.links || []).map(l => ({ ...l }));

  buildFiltersFromData(rawNodes, rawLinks);

  // remove previous
  d3.select(graphContainer.value).select("svg").remove();

  const container = graphContainer.value;
  const width = container.clientWidth || 800;
  const height = container.clientHeight || 600;

  svg = d3.select(graphContainer.value)
    .append("svg")
    .attr("width", "100%")
    .attr("height", "100%")
    .attr("viewBox", `0 0 ${width} ${height}`)
    .call(d3.zoom().scaleExtent([0.15, 2]).on("zoom", (event) => graphGroup.attr("transform", event.transform)));

  svg.append("rect").attr("width", width).attr("height", height).attr("fill", "#0f2a3a");

  graphGroup = svg.append("g");

  // map link endpoints to node objects (required by simulation)
  const nodeById = new Map(rawNodes.map(n => [n.id, n]));
  // Ensure each link references actual node objects and drop links with missing endpoints
  const links = rawLinks
    .map(l => {
      const s = nodeById.get(l.source);
      const t = nodeById.get(l.target);
      if (!s || !t) return null; // skip broken links
      return { source: s, target: t, label: l.label };
    })
    .filter(Boolean);
  const nodes = rawNodes;

  // degree
  const nodeDegree = new Map();
  links.forEach(link => {
    const s = typeof link.source === "object" ? link.source.id : link.source;
    const t = typeof link.target === "object" ? link.target.id : link.target;
    nodeDegree.set(s, (nodeDegree.get(s) || 0) + 1);
    nodeDegree.set(t, (nodeDegree.get(t) || 0) + 1);
  });

  const sizeScale = d3.scaleLinear()
    .domain([1, d3.max([...nodeDegree.values()]) || 1])
    .range([6, 30]);

  const simulation = d3.forceSimulation(nodes)
    .force("link", d3.forceLink(links).id(d => d.id).distance(180))
    .force("charge", d3.forceManyBody().strength(-350))
    .force("center", d3.forceCenter(width / 2, height / 2))
    .force("collision", d3.forceCollide().radius(d => sizeScale(nodeDegree.get(d.id) || 1) + 6));

  // links
  linkSel = graphGroup.append("g")
    .attr("class", "links")
    .selectAll("line")
    .data(links)
    .enter()
    .append("line")
    .attr("stroke", d => edgeColorMap[d.label] || edgeColorMap["default"])
    .attr("stroke-width", 1.5)
    .on("mouseover", function(event, d) {
      // show temporary label
      graphGroup.selectAll(".edge-hover-label").remove();
      // compute midpoint immediately (d.source/d.target are node objects now)
      const midX = (d.source.x + d.target.x) / 2 || 0;
      const midY = (d.source.y + d.target.y) / 2 || 0;
      graphGroup.append("text")
        .datum(d)
        .attr("class", "edge-hover-label")
        .attr("text-anchor", "middle")
        .attr("font-size", "12px")
        .attr("x", midX)
        .attr("y", midY - 8)
        .attr("fill", edgeColorMap[d.label] || edgeColorMap["default"])
        .text(d.label || "")
        .style("pointer-events", "none");
    })
    .on("mouseout", () => graphGroup.selectAll(".edge-hover-label").remove());

  // nodes
  nodeSel = graphGroup.append("g")
    .attr("class", "nodes")
    .selectAll("circle")
    .data(nodes)
    .enter()
    .append("circle")
    .attr("r", d => sizeScale(nodeDegree.get(d.id) || 1))
    .attr("fill", d => d.color || "#888")
    .attr("stroke", "#111")
    .attr("stroke-width", 1)
    .call(d3.drag()
      .on("start", (event, d) => { if (!event.active) simulation.alphaTarget(0.3).restart(); d.fx = d.x; d.fy = d.y; })
      .on("drag", (event, d) => { d.fx = event.x; d.fy = event.y; })
      .on("end", (event, d) => { if (!event.active) simulation.alphaTarget(0); d.fx = null; d.fy = null; })
    )
    .on("click", (event, d) => {
      // open both Wikidata (id) and Wookieepedia if available
      if (d.id) window.open(d.id, "_blank");
      if (d.wookipedia_url) window.open(d.wookipedia_url, "_blank");
      selectedNode.value = d;
      isModalOpen.value = true;
    })
    .on("mouseover", (event, d) => {
      // highlight neighbors
      handleMouseOver(event, d);
    })
    .on("mouseout", handleMouseOut);

  // labels (node)
  labelsSel = graphGroup.append("g")
    .attr("class", "labels")
    .selectAll("text")
    .data(nodes)
    .enter()
    .append("text")
    .attr("text-anchor", "middle")
    .attr("dy", d => - (sizeScale(nodeDegree.get(d.id) || 1) + 6))
    .attr("fill", "#fff")
    .attr("font-size", 11)
    .style("pointer-events", "none")
    .text(d => d.label || "");

  simulation.on("tick", () => {
    linkSel
      .attr("x1", d => d.source.x)
      .attr("y1", d => d.source.y)
      .attr("x2", d => d.target.x)
      .attr("y2", d => d.target.y);

    nodeSel
      .attr("cx", d => d.x)
      .attr("cy", d => d.y);

    labelsSel
      .attr("x", d => d.x)
      .attr("y", d => d.y - (sizeScale(nodeDegree.get(d.id) || 1) + 6));

    // update hover-edge label position if present
    graphGroup.selectAll(".edge-hover-label")
      .attr("x", d => ((d.source && d.source.x) + (d.target && d.target.x)) / 2)
      .attr("y", d => ((d.source && d.source.y) + (d.target && d.target.y)) / 2 - 8);
  });

  // initial visibility based on selected filters
  updateVisibility();

  // expose selections for updateVisibility closure
  // helper functions
  function handleMouseOver(event, d) {
    // compute connected nodes/links
    const connectedNodes = new Set();
    const connectedLinks = new Set();
    rawLinks.forEach(l => {
      if (l.source === d.id || (typeof l.source === "object" && l.source.id === d.id)) {
        connectedNodes.add(l.target);
        connectedLinks.add(l);
      }
      if (l.target === d.id || (typeof l.target === "object" && l.target.id === d.id)) {
        connectedNodes.add(l.source);
        connectedLinks.add(l);
      }
    });

    nodeSel.transition().duration(200).style("opacity", n => (n.id === d.id || connectedNodes.has(n.id)) ? 1 : 0.15);
    labelsSel.transition().duration(200).style("opacity", n => (n.id === d.id || connectedNodes.has(n.id)) ? 1 : 0.15);
    linkSel.transition().duration(200).style("opacity", l => {
      // find link object that matches this line's d; we used link objects in linkSel, so l.label exists
      return (l.source.id === d.id || l.target.id === d.id || connectedLinks.has(l)) ? 1 : 0.06;
    });
  }

  function handleMouseOut() {
    nodeSel.transition().duration(200).style("opacity", 1);
    labelsSel.transition().duration(200).style("opacity", 1);
    linkSel.transition().duration(200).style("opacity", 1);
  }
};

const updateVisibility = () => {
  if (!nodeSel || !linkSel || !labelsSel) return;
  // nodes: visible if their type is selected
  nodeSel.style("display", d => selectedClasses.value.includes(d.type) ? null : "none");
  // links: visible if their label is selected AND both endpoint node types are selected
  linkSel.style("display", d => {
    const labelOk = selectedEdgeLabels.value.includes(d.label);
    const sourceType = (d.source && d.source.type) || rawNodes.find(n => n.id === d.source)?.type;
    const targetType = (d.target && d.target.type) || rawNodes.find(n => n.id === d.target)?.type;
    const endpointsOk = selectedClasses.value.includes(sourceType) && selectedClasses.value.includes(targetType);
    return (labelOk && endpointsOk) ? null : "none";
  });
  // labels: visible only when node labels are enabled AND the node's class is selected
  labelsSel.style("display", d => {
    if (!showNodeLabels.value) return "none";
    return selectedClasses.value.includes(d.type) ? null : "none";
  });
  // edge hover labels will still appear on mouseover; permanent edge text not rendered except hover
};

watch([selectedClasses, selectedEdgeLabels, showNodeLabels], () => {
  updateVisibility();
});

// redraw on resize
const resizeGraph = () => drawGraph();

onMounted(() => {
  drawGraph();
  window.addEventListener("resize", resizeGraph);
});

onUnmounted(() => {
  window.removeEventListener("resize", resizeGraph);
});
</script>

<style scoped>
.graph-container {
  width: 100%;
  height: 100%;
  min-height: 600px;
  position: relative;
  background: linear-gradient(180deg,#07212a 0%,#0f2a3a 100%);
}
.edge-hover-label {
  pointer-events: none;
  font-weight: 600;
  text-shadow: 0 1px 2px rgba(0,0,0,0.7);
}
</style>
